import * as React from 'react';
import { Component } from './type-extensions';

interface ISubWindowMinimizedLayoutProps {
    title: string;
    withExpand?: boolean;
    isExpanded?: boolean;
    isExpandedByDefault?: boolean;
    additionalTitle?: string;
    subWindowClassname?: string;
}
type ISubWindowLayoutProps = React.PropsWithChildren<ISubWindowMinimizedLayoutProps>;

/**
 * parameters, passed to ISubwindowService in order to show new sub-window
 */
interface ISubWindowParameters<T> {
    /**
     * sub-window title
     */
    title: string;
    /**
     * optional: custom class name, appended to root element of sub-window.
     * to be used for feature-specific customization of the whole sub-window
     */
    className?: string;
    /**
     * optional: custom layout of sub-window to be used while sub-window is in the in minimized state
     */
    minimizedLayoutComponent?: Component<ISubWindowMinimizedLayoutProps>;
    /**
     * optional: custom layout of sub-window to be used while sub-window is in the in maximized state
     */
    layoutComponent?: Component<ISubWindowLayoutProps>;
    /**
     * react component (either class or functional), which is being displayed in the sub-window while it is in the maximized state
     */
    componentToHost: Component<T>;
    expandedComponentToHost?: Component<T>;
    /**
     * props, which would be passed to `componentToHost` while rendering
     */
    componentToHostProps: T;
    /**
    * optional: props, which would be open new sub-window as first element
    */
    showAsFirst?: boolean;
    withExpand?: boolean;
    isExpandedByDefault?: boolean;
    additionalTitle?: string;
    onCloseIconClickHandler?: () => void;
    subWindowClassname?: string;
}

enum SubWindowStatus {
    Maximized,
    Minimized,
    Closed
}

/**
 * interface, exposed to external code.
 * E.g. this is something, particular business feature retrieve by calling ISubWindowsService.showSubWindow
 * so then sub-window can be manually manipulated within the particular business feature
 */
interface ISubWindowFacade {
    getUID: () => number;
    getStatus: () => SubWindowStatus;
    minimize: () => void;
    maximize: () => void;
    expand?: () => void;
    close: () => void;
}

/**
 * Provide api for showing components inside the sub-windows
 * Sub-window does not prevents user interactions with the page behind it.
 * There might be a multiple sub-windows shown simultaneously.
 * Sub-windows can be minimized in order to temporary release the space behind them
 *
 * If particular UI view should be shown in modal window - use CommonModal instead of this service.
 */
abstract class ISubWindowsService {
    /**
     * shows common sub-window and return it's facade
     * which can later be re-used for imperative manipulations (close/minimize/maximize)
     * @param subWindowParameters
     */
    public abstract showSubWindow<T>(subWindowParameters: ISubWindowParameters<T>): ISubWindowFacade;
}

export {
    ISubWindowsService,
    ISubWindowParameters,
    ISubWindowMinimizedLayoutProps,
    ISubWindowLayoutProps,
    ISubWindowFacade,
    SubWindowStatus,
};
